/**
 * Media Constraints Generator
 *
 * Generates optimal MediaStreamConstraints based on platform and role (host/guest).
 * Replaces hardcoded constraints scattered across the codebase.
 */

import { PlatformInfo } from './detection';
import { getMediaCapabilities } from './capabilities';

export type CallRole = 'host' | 'guest';

/**
 * Generates optimal video constraints for the platform and role
 */
function getVideoConstraints(
  platform: PlatformInfo,
  role: CallRole
): MediaTrackConstraints | boolean {
  const capabilities = getMediaCapabilities(platform);
  const { width, height } = capabilities.maxVideoResolution;
  const frameRate = capabilities.maxFrameRate;

  const constraints: MediaTrackConstraints = {
    width: { ideal: width, max: width * 2 },
    height: { ideal: height, max: height * 2 },
    frameRate: { ideal: frameRate, max: frameRate },
    aspectRatio: { ideal: 16 / 9 },
  };

  // Guest always uses back-facing camera (environment)
  if (role === 'guest') {
    constraints.facingMode = 'environment';
  }

  // WebView optimizations
  if (platform.isWebView) {
    // Lower resolution for battery life
    constraints.width = { ideal: 640, max: 854 };
    constraints.height = { ideal: 480, max: 640 };
    constraints.frameRate = { ideal: 24, max: 30 };
  }

  return constraints;
}

/**
 * Generates optimal audio constraints for the platform
 */
function getAudioConstraints(platform: PlatformInfo): MediaTrackConstraints | boolean {
  const constraints: MediaTrackConstraints = {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
  };

  // iOS specific: sample rate
  if (platform.isIOS) {
    (constraints as any).sampleRate = 48000;
  }

  // Android WebView: more aggressive echo cancellation
  if (platform.isAndroid && platform.isWebView) {
    constraints.echoCancellation = true;
    constraints.noiseSuppression = true;
  }

  return constraints;
}

/**
 * Generates complete MediaStreamConstraints for getUserMedia()
 */
export function getMediaConstraints(
  platform: PlatformInfo,
  role: CallRole
): MediaStreamConstraints {
  return {
    audio: getAudioConstraints(platform),
    video: getVideoConstraints(platform, role),
  };
}

/**
 * Generates constraints for screen sharing (host only, desktop only)
 */
export function getDisplayMediaConstraints(
  platform: PlatformInfo
): DisplayMediaStreamConstraints {
  if (platform.isMobile) {
    throw new Error('Screen sharing not supported on mobile devices');
  }

  return {
    audio: false, // Don't capture system audio by default
    video: {
      cursor: 'always' as any,
      displaySurface: 'monitor' as any,
      frameRate: { ideal: 30, max: 60 },
      width: { ideal: 1920, max: 3840 },
      height: { ideal: 1080, max: 2160 },
    },
  };
}

/**
 * Generates optimized constraints for camera switching
 * Used when user switches between front/back camera
 */
export function getCameraSwitchConstraints(
  platform: PlatformInfo,
  facingMode: 'user' | 'environment'
): MediaTrackConstraints {
  const baseConstraints = getVideoConstraints(platform, 'host') as MediaTrackConstraints;

  return {
    ...baseConstraints,
    facingMode: { exact: facingMode },
  };
}

/**
 * Platform-specific constants for timeouts and retries
 */
export const PLATFORM_CONSTANTS = {
  // Time to wait for loadedmetadata event (ms)
  VIDEO_LOAD_TIMEOUT: 5000,

  // Time to wait between retry attempts (ms)
  RETRY_DELAY_BASE: 100,

  // Maximum number of retry attempts for video playback
  MAX_PLAYBACK_RETRIES: 3,

  // Maximum number of retry attempts for getUserMedia
  MAX_GETUSERMEDIA_RETRIES: 2,

  // Delay before refreshing video stream (ms)
  STREAM_REFRESH_DELAY: 50,

  // WebRTC connection timeout (ms)
  WEBRTC_CONNECTION_TIMEOUT: 30000,

  // ICE gathering timeout (ms)
  ICE_GATHERING_TIMEOUT: 10000,
} as const;

/**
 * Gets platform-specific timeout for an operation
 */
export function getPlatformTimeout(
  platform: PlatformInfo,
  operation: keyof typeof PLATFORM_CONSTANTS
): number {
  const baseTimeout = PLATFORM_CONSTANTS[operation];

  // Mobile/WebView needs more time for some operations
  if (platform.isMobile || platform.isWebView) {
    // Increase timeouts by 50% on mobile
    return Math.floor(baseTimeout * 1.5);
  }

  return baseTimeout;
}
